import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { TrackingColumnsContext } from "contexts/TrackingColumns";
import { useContext, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  AutomatedContactProperty,
  OutreachContactStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import Drawer from "components/Drawer";
import PartnershipData from "features/Influencer/Discover/DetailedBrandView/components/PartnershipData";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import BrandTitle from "features/Influencer/Discover/Shared/BrandTitle";
import { isDemoUser } from "utils/demo";
import { isPage } from "utils/general";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { hideEmail } from "utils/string";

import ArchiveButton from "../NewOutreachTable/ContactRow/components/ArchiveButton";
import ContactInsights from "../NewOutreachTable/ContactRow/components/ContactInsights";
import ContactSource from "../NewOutreachTable/ContactRow/components/ContactSource";
import IconAction from "../NewOutreachTable/ContactRow/components/IconAction";
import SelectStatus from "../NewOutreachTable/ContactRow/components/SelectStatus";
import EmailThreadRow from "./EmailThreadRow";
import OutreachEmail from "./OutreachEmail";
import ScheduledEmail from "./ScheduledEmail";
import ScheduledEmailRow from "./ScheduledEmailRow";
import styles from "./styles";

export default function OutreachContactDrawer() {
  const { setAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreenEmail = isLargeScreen;
  const [expanded, setExpanded] = useState<boolean>(false);

  const { outreachContactId: id } = useParams();
  const location = useLocation();
  const outreachContactId = Number(id);
  const {
    currentContact,
    currentContactEmailThreads,
    fetchContact,
    fetchEmailThreads,
    currentContactLoading,
    currentContactThreadsLoading,
    currentContactPageToken,
    currentContactScheduledEmailsLoading,
    currentContactScheduledEmails,
    fetchScheduledEmails,
    missingIntegration,
    currentSidebarOption,
  } = useContext(OutreachContactsContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const isDemoAccount = isDemoUser(currentOrg);

  const { contactViewId, campaignId } = useParams();
  const fromCampaign = Boolean(campaignId);
  const { contactProperties, getContactProperties } = useContext(
    TrackingColumnsContext,
  );

  const closeDrawer = () => {
    if (isPage(routes.actionItem)) {
      navigate(`/${routes.home}`);
    } else if (fromCampaign) {
      navigate(`/${routes.campaignView}/${campaignId}`);
    } else if (isPage(routes.inbox)) {
      navigate(`/${routes.inbox}/${currentSidebarOption?.key}`);
    } else {
      if (contactViewId) {
        navigate(`/${routes.trackingView}/${contactViewId}`);
      } else {
        navigate(`/dashboard/${routes.tracking}`);
      }
    }
  };

  const fetchMore = () => {
    fetchEmailThreads(outreachContactId, false);
  };

  useEffect(() => {
    if (isNaN(outreachContactId)) {
      return;
    }
    fetchContact(outreachContactId);
    fetchEmailThreads(outreachContactId, true);
    fetchScheduledEmails(outreachContactId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachContactId, currentOrg?.id]);

  useEffect(() => {
    getContactProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const latestPendingEmail = currentContactEmailThreads?.find(
    (x) => x.pendingEmailsCount > 0,
  );
  const switchTab =
    !fromCampaign &&
    outreachContactId &&
    latestPendingEmail?.id &&
    latestPendingEmail?.id !== currentContact?.lastGmailThreadId &&
    currentSidebarOption;
  useEffect(() => {
    if (switchTab) {
      navigate(
        `/${routes.inbox}/${currentSidebarOption.key}/${outreachContactId}/email/${latestPendingEmail?.id}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchTab]);

  // Auto-open the contact's latest thread when the URL lacks /email/{Y}
  // (e.g. post-send Scheduled → Sent redirect). Skipped on md-down because
  // closing the full-screen thread there strips /email/{Y} and we'd loop.
  const hasThreadInUrl = location.pathname.includes("/email/");
  useEffect(() => {
    if (
      !fromCampaign &&
      !isLargeScreen &&
      !hasThreadInUrl &&
      currentContact?.lastGmailThreadId &&
      currentSidebarOption &&
      !switchTab
    ) {
      navigate(
        `/${routes.inbox}/${currentSidebarOption.key}/${outreachContactId}/email/${currentContact.lastGmailThreadId}`,
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLargeScreen,
    hasThreadInUrl,
    currentContact?.lastGmailThreadId,
    currentSidebarOption,
    switchTab,
  ]);

  if (isNaN(outreachContactId) || (!currentContactLoading && !currentContact)) {
    return (
      <Navigate
        to={
          fromCampaign
            ? `/${routes.campaignView}/${campaignId}`
            : `/${routes.inbox}`
        }
        replace
      />
    );
  }

  const renderErrorText = () => {
    if (currentContactThreadsLoading) {
      return <></>;
    } else if (
      currentContactEmailThreads.length === 0 &&
      currentContact &&
      !currentContact.email
    ) {
      return (
        <Grid item xs sx={{ maxWidth: "100%" }}>
          <Typography component="div" variant="caption" color="textSecondary">
            This contact has no email address or domain associated with it.
          </Typography>
        </Grid>
      );
    } else if (missingIntegration) {
      return (
        <Alert severity="error">
          Log in to Google on your{" "}
          <Link style={{ color: "black" }} to={`/${routes.settings}`}>
            settings page
          </Link>{" "}
          to continue using this feature
        </Alert>
      );
    } else if (currentContactEmailThreads.length === 0) {
      return (
        <Grid item xs sx={{ maxWidth: "100%" }}>
          <Typography component="div" variant="caption" color="textSecondary">
            No threads could be found in your inbox for this contact. Deleted
            threads are not shown here.
          </Typography>
        </Grid>
      );
    } else {
      return <></>;
    }
  };

  const bentoBrand =
    currentContact &&
    (currentContact.bentoBrand || {
      createdAt: currentContact.createdAt || 0,
      id: 0,
      brandName: currentContact.brand?.name || "",
      normalizedName: currentContact.brand?.name || "",
      instagramUsername: currentContact.brand?.instagramUsername,
      website: currentContact.brand?.website,
      compensationTypes: currentContact.brand?.compensationTypes,
      tags: "",
      latestOutreachTemplateId: null,
    });

  const stageProperty = contactProperties.find(
    (p) => p.automatedProperty === AutomatedContactProperty.stage,
  );

  let stageValue = null;

  if (stageProperty?.id && currentContact?.propertyValues?.[stageProperty.id]) {
    const propertyValue = currentContact?.propertyValues?.[stageProperty.id];
    stageValue = stageProperty.selectOptions?.find(
      (option) => option.id === propertyValue,
    );
  }

  const isAdmin =
    Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN)) ||
    process.env.NODE_ENV === "development";

  return (
    <>
      <Drawer
        width={{ xs: "auto", md: "90%", lg: "80%" }}
        open={true}
        onClose={closeDrawer}
      >
        <Box sx={styles.root}>
          <Grid container alignItems="flex-start">
            <Grid item xs={12}>
              <Box sx={{ mt: 2, ml: 1 }}>
                <Button color="secondary" size="small" onClick={closeDrawer}>
                  <Box
                    component="i"
                    className="fa-solid fa-chevron-left"
                    sx={{ mr: 1 }}
                  />{" "}
                  Back
                </Button>
              </Box>
            </Grid>

            <Grid
              container
              alignItems="flex-start"
              item
              xs={12}
              md={fullScreenEmail ? 12 : 6}
              lg={fullScreenEmail ? 12 : 5}
              xl={4}
              spacing={1}
              sx={styles.contactInfoContainer}
            >
              {currentContactLoading && (
                <Grid
                  container
                  sx={styles.progressContainer}
                  justifyContent="center"
                  gap={2}
                >
                  <Skeleton
                    animation="wave"
                    variant="rounded"
                    height={20}
                    width="95%"
                  />
                  <Skeleton
                    animation="wave"
                    variant="rounded"
                    height={40}
                    width="95%"
                    sx={{ mb: 3 }}
                  />
                </Grid>
              )}
              {!currentContactLoading && currentContact && bentoBrand && (
                <>
                  <Grid
                    item
                    xs={12}
                    onClick={() => {
                      setExpanded(!expanded);
                    }}
                  >
                    <BrandTitle
                      bentoBrand={bentoBrand}
                      source={BrandCardSource.THREAD_BRAND}
                      isCardView
                      expanded={expanded}
                      hideWrongInfoButton={isDemoAccount}
                    />
                  </Grid>

                  {!isDemoAccount ? (
                    <Grid item xs={12} sx={{ mb: 1 }}>
                      <Typography gutterBottom>
                        <strong>Contact</strong>{" "}
                        {currentContact?.fromVa && (
                          <Chip
                            size="small"
                            sx={[styles.chip]}
                            label="From VA"
                          />
                        )}
                      </Typography>
                      {currentContact.contactName && (
                        <Typography noWrap sx={styles.clipText} component="div">
                          {currentContact.contactName}
                        </Typography>
                      )}
                      {currentContact.contactTitle && (
                        <Typography
                          noWrap
                          sx={styles.clipText}
                          color="textSecondary"
                          component="div"
                          variant="caption"
                        >
                          {currentContact.contactTitle}
                        </Typography>
                      )}
                      <Typography
                        noWrap
                        sx={styles.clipText}
                        component="div"
                        color="textSecondary"
                        variant="caption"
                      >
                        {currentContact.email
                          ? currentContact.status ===
                            OutreachContactStatus.pending
                            ? hideEmail(currentContact.email)
                            : currentContact.email
                          : ""}
                      </Typography>
                      <Grid container sx={{ mt: 1 }} alignItems="center">
                        {stageValue && stageProperty && (
                          <SelectStatus
                            contact={currentContact}
                            stage={stageValue}
                            contactProperty={stageProperty}
                            setAlert={setAlert}
                          />
                        )}
                        <Box sx={{ mr: 1 }} component="span">
                          <IconAction
                            fieldName="isPinned"
                            contact={currentContact}
                            setAlert={setAlert}
                          />
                        </Box>
                        <ArchiveButton
                          contact={currentContact}
                          setAlert={setAlert}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sx={{ mb: 1 }}></Grid>
                  )}

                  <PartnershipData
                    title={
                      isDemoAccount
                        ? "Why this brand was pitched?"
                        : "Brand Insights"
                    }
                    noDataHide
                    bentoBrand={bentoBrand}
                    showGrid
                    gridProps={{
                      xs: 12,
                      md: isDemoAccount ? 12 : true,
                      sx: { mb: 1 },
                    }}
                  />
                  {currentContact.bentoContact && (
                    <ContactInsights
                      title={
                        isDemoAccount
                          ? "Why this contact was selected?"
                          : "Contact Insights"
                      }
                      bentoContact={currentContact.bentoContact}
                    />
                  )}
                  {isAdmin && !isDemoAccount && (
                    <ContactSource contact={currentContact} />
                  )}
                </>
              )}

              {!(
                isDemoAccount &&
                !currentContact?.latestSequenceSentCount &&
                currentContact?.latestSequenceNextSendAt
              ) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  {currentContactScheduledEmailsLoading && (
                    <Grid
                      container
                      sx={styles.progressContainer}
                      justifyContent="center"
                      gap={2}
                    >
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={30}
                        width="95%"
                      />

                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={50}
                        width="95%"
                      />
                    </Grid>
                  )}
                  {!currentContactScheduledEmailsLoading &&
                    currentContactScheduledEmails.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h5" paragraph>
                            Scheduled Outreach Emails
                          </Typography>

                          <Grid container item xs={12}>
                            {currentContactScheduledEmails.map(
                              (scheduledEmail) => (
                                <Grid item xs={12} key={scheduledEmail.id}>
                                  <ScheduledEmailRow
                                    scheduledEmail={scheduledEmail}
                                    setAlert={setAlert}
                                  />
                                </Grid>
                              ),
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                      </>
                    )}

                  <Grid item xs={12}>
                    <Typography variant="h5" paragraph>
                      Recent Email Threads
                    </Typography>

                    <Grid
                      container
                      item
                      xs={12}
                      sx={{ mt: 2 }}
                      justifyContent="center"
                    >
                      {renderErrorText()}
                    </Grid>

                    <Grid container item xs={12}>
                      {currentContactEmailThreads.map((emailThread) => (
                        <Grid item xs={12} key={emailThread.id}>
                          <EmailThreadRow
                            selected={location.pathname.includes(
                              emailThread.id,
                            )}
                            gmailThread={emailThread}
                            fromSidebar={true}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    {!currentContactThreadsLoading &&
                      currentContactPageToken && (
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{ mt: 2 }}
                          justifyContent="center"
                        >
                          <Grid item xs="auto">
                            <Button onClick={fetchMore} variant="outlined">
                              View More
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    {currentContactThreadsLoading && (
                      <Grid
                        container
                        sx={styles.progressContainer}
                        justifyContent="center"
                        gap={2}
                      >
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={40}
                          width="100%"
                        />
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={40}
                          width="100%"
                        />
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={40}
                          width="100%"
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
            {!fullScreenEmail && (
              <Grid
                item
                xs={12}
                md={6}
                lg={7}
                xl={8}
                sx={styles.emailContainer}
              >
                <Routes>
                  <Route
                    path="/email/:threadId/*"
                    element={<OutreachEmail setAlert={setAlert} />}
                  />
                  <Route
                    path="/scheduled/:scheduledId/*"
                    element={<ScheduledEmail />}
                  />
                  <Route path="/scheduled/*" element={<ScheduledEmail />} />
                </Routes>
              </Grid>
            )}
          </Grid>
        </Box>
      </Drawer>
      {fullScreenEmail && (
        <Routes>
          <Route
            path="/email/:threadId/*"
            element={
              <Box sx={styles.root}>
                <OutreachEmail setAlert={setAlert} />
              </Box>
            }
          />
          <Route
            path="/scheduled/:scheduledId/*"
            element={
              <Box sx={styles.root}>
                <ScheduledEmail />
              </Box>
            }
          />
          <Route
            path="/scheduled/*"
            element={
              <Box sx={styles.root}>
                <ScheduledEmail />
              </Box>
            }
          />
        </Routes>
      )}
    </>
  );
}
